import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, input, OnDestroy, OnInit, signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { ConfirmDialogService, CssHelper } from '@tailormap-viewer/shared';
import { from, Observable, toArray } from 'rxjs';
import {
  selectEditCreateNewOrCopyFeatureActive, selectEditDialogCollapsed, selectEditDialogVisible, selectEditFeatures, selectEditMapCoordinates,
  selectEditOpenedFromFeatureInfo, selectLoadingEditFeatures, selectSelectedEditFeature,
} from '../state/edit.selectors';
import { combineLatest, concatMap, filter, map, of, switchMap, take } from 'rxjs';
import {
  editNewlyCreatedFeature, expandCollapseEditDialog, hideEditDialog, setEditActive, setSelectedEditFeature, updateEditFeature,
} from '../state/edit.actions';
import {
  AttachmentMetadataModel, BaseComponentTypeEnum, EditConfigModel, FeatureModelAttributes, LayerDetailsModel, UniqueValuesService,
} from '@tailormap-viewer/api';
import { ApplicationLayerService } from '../../../map/services/application-layer.service';
import { FeatureWithMetadataModel } from '../models/feature-with-metadata.model';
import { EditFeatureService } from '../services/edit-feature.service';
import { selectViewerId } from '../../../state/core.selectors';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { EditMapToolService } from '../services/edit-map-tool.service';
import { FeatureUpdatedService } from '../../../services/feature-updated.service';
import { hideFeatureInfoDialog, reopenFeatureInfoDialog } from '../../feature-info/state/feature-info.actions';
import { ComponentConfigHelper } from '../../../shared/helpers/component-config.helper';
import { withLatestFrom } from 'rxjs/operators';
import { MenubarService } from '../../menubar';
import { MapService } from '@tailormap-viewer/map';
import { EditFormComponent } from '../edit-form/edit-form.component';
import { EditAttachmentsFormComponent } from '../edit-attachments-form/edit-attachments-form.component';
import { MatButton } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { EditSelectFeatureComponent } from '../edit-select-feature/edit-select-feature.component';
import { DialogComponent } from '../../../shared/components/dialog/dialog.component';
import { NgTemplateOutlet, AsyncPipe } from '@angular/common';
import { MapSpinnerComponent } from '../../../map/components/map-spinner/map-spinner.component';

@Component({
    selector: 'tm-edit-dialog',
    templateUrl: './edit-dialog.component.html',
    styleUrls: ['./edit-dialog.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        EditFormComponent,
        EditAttachmentsFormComponent,
        MatButton,
        MatProgressSpinner,
        EditSelectFeatureComponent,
        DialogComponent,
        NgTemplateOutlet,
        MapSpinnerComponent,
        AsyncPipe,
    ],
})
export class EditDialogComponent implements OnInit, OnDestroy {
  private store$ = inject(Store);
  private editMapToolService = inject(EditMapToolService);
  private applicationLayerService = inject(ApplicationLayerService);
  private editFeatureService = inject(EditFeatureService);
  private destroyRef = inject(DestroyRef);
  private featureUpdatedService = inject(FeatureUpdatedService);
  private confirmService = inject(ConfirmDialogService);
  private uniqueValuesService = inject(UniqueValuesService);
  private cdr = inject(ChangeDetectorRef);
  private menubarService = inject(MenubarService);
  private mapService = inject(MapService);


  public inMobilePanel = input<boolean>(false);

  private viewerId = toSignal(this.store$.select(selectViewerId).pipe(map(id => id || '')), { initialValue: '' });
  public dialogOpen$;
  public dialogCollapsed$;
  public isCreateFeature$;
  public currentFeature$;
  public dialogTitle$;
  public layerDetails$;
  public selectableFeature$;
  public editFeatures = this.store$.selectSignal(selectEditFeatures);

  public creatingSavingFeature = signal(false);
  public removingFeature = signal(false);

  public panelWidth = 300;
  private bodyMargin = CssHelper.getCssVariableValueNumeric('--body-margin');
  public panelWidthMargin = CssHelper.getCssVariableValueNumeric('--menubar-width') + (this.bodyMargin * 2);

  public loadingEditFeatureInfo$;
  public editCoordinates$;

  public updatedAttributes: FeatureModelAttributes | null = null;

  private newAttachments = new Map<string, File[]>();
  private deletedAttachmentIds = new Set<string>();
  public attachments$: Observable<Map<string, Array<AttachmentMetadataModel & { url: string }>>>;

  public formValid: boolean = false;

  private clearCacheValuesAfterSave = new Set<string>();
  private closeDialogAfterAddingFeature = false;

  constructor() {
    this.dialogOpen$ = this.store$.select(selectEditDialogVisible);
    this.dialogCollapsed$ = this.store$.select(selectEditDialogCollapsed);
    this.loadingEditFeatureInfo$ = this.store$.select(selectLoadingEditFeatures);
    this.editCoordinates$ = this.store$.select(selectEditMapCoordinates);
    this.currentFeature$ = this.store$.select(selectSelectedEditFeature);
    this.dialogTitle$ = this.currentFeature$.pipe(
      map(feature => feature?.feature.__fid !== 'new'
        ? $localize `:@@core.edit.edit-feature:Edit feature`
        : $localize `:@@core.edit.add-new-feature:Add new feature`));
    this.isCreateFeature$ = this.store$.select(selectEditCreateNewOrCopyFeatureActive);
    this.selectableFeature$ = combineLatest([
      this.store$.select(selectEditFeatures),
      this.store$.select(selectSelectedEditFeature),
    ]).pipe(
        map(([ features, selectedFeature ]) => {
          if (selectedFeature) {
            return [];
          }
          return features;
        }),
    );
    this.layerDetails$ = this.currentFeature$
      .pipe(
        filter((feature): feature is FeatureWithMetadataModel => !!feature),
        switchMap(feature => {
          return this.applicationLayerService.getLayerDetails$(feature.feature.layerId);
        }),
      );
    this.currentFeature$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.store$.dispatch(hideFeatureInfoDialog());
        this.resetChanges();
      });
    this.attachments$ = this.currentFeature$.pipe(
      map(feature => {
        if (!feature || feature.feature.__fid === 'new') {
          return new Map();
        } else {
          return (feature.feature.attachments || []).map(attachment => ({
            ...attachment,
            url: this.editFeatureService.getAttachmentUrl(this.viewerId(), feature.feature.layerId, attachment.attachmentId),
          })).reduce((attachmentsByAttributeName, attachment) => {
            if (attachmentsByAttributeName.has(attachment.attributeName)) {
              attachmentsByAttributeName.get(attachment.attributeName)?.push(attachment);
            } else {
              attachmentsByAttributeName.set(attachment.attributeName, [attachment]);
            }
            return attachmentsByAttributeName;
          }, new Map<string, Array<AttachmentMetadataModel & { url: string }>>());
        }
      }),
    );

    this.editMapToolService.allEditGeometry$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(geometry => {
      this.geometryChanged(geometry, false);
    });

    ComponentConfigHelper.useInitialConfigForComponent<EditConfigModel>(
      this.store$,
      BaseComponentTypeEnum.EDIT,
      config => {
        this.closeDialogAfterAddingFeature = config.closeAfterAddFeature;
      },
    );
  }

  public ngOnDestroy(): void {
    if (this.inMobilePanel()) {
      this.mapService.enableAutoEnabledTools();
      this.closeDialog();
    }
  }

  public ngOnInit() {
    if (this.inMobilePanel()) {
      this.mapService.disableAllTools();
      this.dialogTitle$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(dialogTitle => {
          this.menubarService.setDialogTitle(dialogTitle);
        });
    }
  }

  private resetChanges() {
    this.newAttachments = new Map();
    this.deletedAttachmentIds = new Set();
    this.updatedAttributes = null;
    this.formValid = false;
    this.clearCacheValuesAfterSave = new Set();
  }

  private setAttributeUpdated(attribute: string, value: any) {
    if (this.updatedAttributes === null) {
      this.updatedAttributes = {};
    }
    this.updatedAttributes[attribute] = value;
}

  public closeDialog(reopenFeatureInfo = true) {
    this.store$.dispatch(hideEditDialog());
    this.store$.select(selectEditOpenedFromFeatureInfo).pipe(take(1)).subscribe(openedFromFeatureInfo => {
      if (openedFromFeatureInfo && reopenFeatureInfo) {
        this.store$.dispatch(setEditActive({ active: false }));
        this.store$.dispatch(reopenFeatureInfoDialog());
      }
    });
  }

  public expandCollapseDialog() {
    this.store$.dispatch(expandCollapseEditDialog());
  }

  public attachmentsUpdated() {
    // Files could have been selected and then deselected, resulting in an empty array value
    return Array.from(this.newAttachments.values()).some(files => files.length !== 0)
      || this.deletedAttachmentIds.size !== 0;
  }

  public addOrSaveDisabled(currentFeature: FeatureWithMetadataModel | null, geometryAttribute: string) {
    const geometryValue = this.getGeometryValue(currentFeature, geometryAttribute);
    const hasGeometry = !(geometryValue === null || geometryValue === undefined ||
                          (typeof geometryValue === 'string' && geometryValue.trim() === ''));
    return !hasGeometry || !(this.formValid || this.attachmentsUpdated()) || this.creatingSavingFeature();
  }

  private getGeometryValue(currentFeature: FeatureWithMetadataModel | null, geometryAttribute: string) {
    if (this.updatedAttributes && Object.hasOwn(this.updatedAttributes, geometryAttribute)) {
      return this.updatedAttributes[geometryAttribute];
    }
    return currentFeature?.feature.attributes[geometryAttribute];
  }

  public save(layerId: string, info: { feature: FeatureWithMetadataModel; details: LayerDetailsModel })  {
    const currentFeature = info.feature;
    const updatedFeature = this.updatedAttributes;
    if (!updatedFeature && !this.attachmentsUpdated()) {
      this.resetChanges();
      return;
    }
    this.uniqueValuesService.clearCaches(Array.from(this.clearCacheValuesAfterSave));
    this.creatingSavingFeature.set(true);

    const updateFeature$ = updatedFeature
      ? this.editFeatureService.updateFeature$(this.viewerId(), layerId, {
          __fid: currentFeature.feature.__fid,
          attributes: updatedFeature,
        })
      : of(null);

    updateFeature$.pipe(
      concatMap(() => this.uploadAttachments$(this.viewerId(), layerId, currentFeature.feature.__fid, info.details)),
      concatMap(() => this.deleteAttachments$(this.viewerId(), layerId, this.deletedAttachmentIds)),
      concatMap(() => this.editFeatureService.getFeature$(this.viewerId(), layerId, currentFeature.feature.__fid)),
      withLatestFrom(this.store$.select(selectEditOpenedFromFeatureInfo)),
    ).subscribe(([ feature, openedFromFeatureInfo ]) => {
      if (feature) {
        this.store$.dispatch(updateEditFeature({ feature, layerId }));
        this.featureUpdatedService.updatedFeature(layerId, feature.__fid);
        this.resetChanges();
        if (openedFromFeatureInfo) {
          this.store$.dispatch(setEditActive({ active: false }));
          this.store$.dispatch(reopenFeatureInfoDialog());
        }
      }
      this.creatingSavingFeature.set(false);
    });
  }

  public add(layerId: string, info: { feature: FeatureWithMetadataModel; details: LayerDetailsModel }) {
    const updatedFeature = this.updatedAttributes;
    if (!updatedFeature) {
      return;
    }
    this.creatingSavingFeature.set(true);

    this.editFeatureService.createFeature$(this.viewerId(), layerId, {
      __fid: '',
      attributes: updatedFeature,
    }).pipe(
      concatMap(result => {
        if (!result.success || !result.feature || !this.attachmentsUpdated()) {
          return of(result);
        } else {
          const fid = result.feature.__fid;
          return this.uploadAttachments$(this.viewerId(), layerId, result.feature.__fid, info.details).pipe(
            concatMap(() => this.editFeatureService.getFeature$(this.viewerId(), layerId, fid)),
            map(feature => ({ ...result, feature })),
          );
        }
      })).subscribe(result => {
        this.creatingSavingFeature.set(false);
        if (!result.success) {
          return;
        }
        this.featureUpdatedService.updatedFeature(layerId, result.feature?.__fid);
        this.resetChanges();
        if (this.closeDialogAfterAddingFeature || !result.feature) {
          this.closeDialog();
        } else {
          this.store$.dispatch(editNewlyCreatedFeature({ feature: { ...result.feature, layerId } }));
        }
      });
  }

  private uploadAttachments$(viewerId: string, layerId: string, featureId: string, details: LayerDetailsModel) {
    const uploads = Array.from(this.newAttachments.entries()).flatMap(([ attribute, files ]) =>
      files.map(file => ({ attribute, file })),
    ).filter(({ attribute, file }) => {
      const attachmentAttribute = details.attachmentAttributes.find(att => att.attributeName === attribute);
      if (!attachmentAttribute) {
        return false;
      }
      return !(attachmentAttribute.maxAttachmentSize && file.size > attachmentAttribute.maxAttachmentSize);

    });
    if (uploads.length === 0) {
      return of(true);
    }
    return from(uploads).pipe(
      concatMap(upload => this.editFeatureService.addAttachment$(
        viewerId,
        layerId,
        featureId,
        upload.attribute,
        upload.file,
      )),
      toArray(),
      map(() => true),
    );
  }

  private deleteAttachments$(viewerId: string, layerId: string, attachmentIds: Set<string>) {
    if (attachmentIds.size === 0) {
      return of(null);
    }
    return from(attachmentIds.values()).pipe(
      concatMap(attachmentId => this.editFeatureService.deleteAttachment$(viewerId, layerId, attachmentId)),
      toArray(),
      map(() => null),
    );
  }

  public delete(layerId: string, currentFeature: FeatureWithMetadataModel) {
    const featureId = currentFeature.feature.__fid;
    this.confirmService.confirm$(
      $localize `:@@core.edit.delete-feature-confirm:Delete feature`,
      $localize `:@@core.edit.delete-feature-confirm-message:Are you sure you want to delete this feature? This cannot be undone.`,
      true,
    ).pipe(
      filter(confirm => confirm),
      concatMap(() => {
        this.removingFeature.set(true);
        return this.editFeatureService.deleteFeature$(this.viewerId(), layerId, currentFeature.feature.__fid);
      }),
    ).subscribe(success => {
      if (success) {
        this.featureUpdatedService.updatedFeature(layerId, featureId);
        this.closeDialog(false);
      }
      this.removingFeature.set(false);
    });
  }

  public featureChanged($event: { attribute: string; value: any; invalid?: boolean }) {
    this.formValid = !$event.invalid;
    this.setAttributeUpdated($event.attribute, $event.value);
  }

  public geometryChanged(geometry: string | null, newFeature: boolean) {
    if (!this.currentFeature$ || geometry === null) {
      return;
    }
    this.currentFeature$.pipe(
      take(1),
      switchMap((feature: FeatureWithMetadataModel | null) => {
        if (!feature) {
          return of(null);
        }
        return this.applicationLayerService.getLayerDetails$(feature.feature.layerId);
      }))
      .subscribe(layerDetails => {
        if (layerDetails) {
          if (!newFeature && this.updatedAttributes === null) {
            // Edited feature but only geometry edited, set this so Save button is enabled
            this.formValid = true;
            this.cdr.detectChanges();
          }
          this.setAttributeUpdated(layerDetails.details.geometryAttribute, geometry);
        }
      });
  }

  public clearUniqueValuesCacheAfterSave(uniqueValueCacheKey: string) {
    this.clearCacheValuesAfterSave.add(uniqueValueCacheKey);
  }

  public onNewAttachmentsChanged(newAttachments: Map<string, File[]>) {
    this.newAttachments = newAttachments;
  }

  public onDeletedAttachmentsChanged(deletedAttachmentIds: Set<string>) {
    this.deletedAttachmentIds = deletedAttachmentIds;
  }

  public backToFeatureList() {
    this.store$.dispatch(setSelectedEditFeature({ fid: null }));
  }

}
